// Drifting marigold petals and candle sparks for the site-wide background layer (see PetalField).

const AREA_PER_PARTICLE = 16000; // px² of window per particle
const MIN_PARTICLES = 18;
const MAX_PARTICLES = 90;
const PETAL_SHARE = 0.3; // the rest are glowing sparks
const WRAP_MARGIN = 20;
const REPEL_RADIUS = 120;
const REPEL_STRENGTH = 100; // px per second right next to the pointer

/** How many particles to draw for a window of this size. */
export const petalCount = (width, height) =>
  Math.round(Math.min(MAX_PARTICLES, Math.max(MIN_PARTICLES, (width * height) / AREA_PER_PARTICLE)));

/** A petal or spark at a random spot of the window; `random` is injectable for tests. */
export const createParticle = (width, height, random = Math.random) => {
  const isPetal = random() < PETAL_SHARE;
  return {
    x: random() * width,
    y: random() * height,
    isPetal,
    size: isPetal ? 9 + random() * 8 : 2 + random() * 4,
    speed: 10 + random() * 22, // px per second, upwards
    sway: random() * Math.PI * 2,
    spin: (random() - 0.5) * 1.6,
    angle: random() * Math.PI * 2,
    alpha: isPetal ? 0.55 + random() * 0.3 : 0.25 + random() * 0.35,
  };
};

/**
 * Advances a particle by `dt` seconds: it rises, sways, spins and is nudged away from the pointer; once it leaves
 * the top it re-enters at a random spot of the bottom edge. Returns a new particle.
 */
export const stepParticle = (particle, { dt, time, width, height, pointer, random = Math.random }) => {
  let x = particle.x + Math.sin(time * 0.35 + particle.sway) * 14 * dt;
  let y = particle.y - particle.speed * dt;
  if (pointer) {
    const dx = x - pointer.x;
    const dy = y - pointer.y;
    const distance = Math.hypot(dx, dy);
    if (distance > 1 && distance < REPEL_RADIUS) {
      const push = (1 - distance / REPEL_RADIUS) * REPEL_STRENGTH * dt;
      x += (dx / distance) * push;
      y += (dy / distance) * push;
    }
  }
  if (y < -WRAP_MARGIN) {
    y = height + WRAP_MARGIN;
    x = random() * width;
  }
  if (x < -WRAP_MARGIN) x = width + WRAP_MARGIN;
  else if (x > width + WRAP_MARGIN) x = -WRAP_MARGIN;
  return { ...particle, x, y, angle: particle.angle + particle.spin * dt };
};
